import { TFunction } from "i18next";
import * as yup from "yup";

import { OtherEnrollmentConditionId, OtherFormOfTeachingId, OtherKnowledgeCheckId } from "../ProgramRequest";
import { QualificationType } from "./QualificationType";

export interface ProgramNameValidationSchemeProps {
  t: TFunction;
}

const otherTextArrayRequiredIfOtherSelected = (
  requiredMsg: string,
  otherId: number,
  idsField: string,
) =>
  yup.array().of(yup.string()).when(["programWithMinisterResolution", idsField], {
    is: (programWithMinisterResolution: boolean, ids: number[]) =>
      programWithMinisterResolution && !!ids?.includes(otherId),
    then: yup.array().of(yup.string().trim().required(requiredMsg)).min(1, requiredMsg),
    otherwise: yup.array().of(yup.string()),
  });

export const ProgramNameValidationScheme = ({
  t,
}: ProgramNameValidationSchemeProps) => {
  const requiredMsg = t("validations.required");
  const requiredAtLeastOne = t("validations.requiredAtLeastOne");
  const positiveNumberOrZeroMsg = t("validations.positiveNumberOrZero");
  const numberLessThanTousend = t("validations.numberLessThan", { nr: 6000 });
  const levelValidation = t("request.levelValidation");
  const csvetValidation = t("request.csvetValidation");

  const max255Msg = t("validations.maximalStringLength", { maxLength: 255 });

  const learningSetSchema = yup.object().shape({
    name: yup.string().required(requiredMsg).max(255, max255Msg),
    code: yup.string().required(requiredMsg).max(255, max255Msg),
    level: yup.number().required(requiredMsg).min(2, levelValidation).max(6, levelValidation).typeError(requiredMsg),
    csvet: yup.number().required(requiredMsg).min(1, csvetValidation).typeError(requiredMsg),
  });

  const moduleSchema = yup.object().shape({
    name: yup.string().required(requiredMsg).max(255, max255Msg),
    learningSets: yup.array().of(learningSetSchema).min(1, requiredMsg),
  });

  const ministerDetailSchema = yup
    .object()
    .required(requiredMsg)
    .shape({
      hkoLevelText: yup.string().trim().required(requiredMsg),
      educationSectorText: yup.string().trim().required(requiredMsg),
      educationSubSectorText: yup.string().trim().required(requiredMsg),
      guidedLearningDuration: yup.number().min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend),
      independentLearningDuration: yup.number().min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend),
      workLearningDuration: yup.number().min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend),
      formOfTeachingIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
      knowledgeCheckIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
      enrollmentConditionIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
      otherFormsOfTeaching: otherTextArrayRequiredIfOtherSelected(
        requiredMsg,
        OtherFormOfTeachingId,
        "ministerDetail.formOfTeachingIds",
      ),
      otherKnowledgeChecks: otherTextArrayRequiredIfOtherSelected(
        requiredMsg,
        OtherKnowledgeCheckId,
        "ministerDetail.knowledgeCheckIds",
      ),
      otherEnrollmentConditions: otherTextArrayRequiredIfOtherSelected(
        requiredMsg,
        OtherEnrollmentConditionId,
        "ministerDetail.enrollmentConditionIds",
      ),
      competences: yup.array().of(yup.string().trim().required(requiredMsg)).min(1, requiredAtLeastOne),
      modules: yup.array().of(moduleSchema).min(1, requiredAtLeastOne),
    });

  return yup.object().shape({
    name: yup.string().required(requiredMsg),
    programTypeId: yup.number().nullable().required(requiredMsg).min(1, requiredMsg),
    dateTo: yup.date().required(requiredMsg).typeError(requiredMsg),
    hkoLevelId: yup.number().nullable().when("programWithMinisterResolution", {
      is: true,
      then: yup.number().nullable(),
      otherwise: yup.number().nullable().required(requiredMsg).min(1, requiredMsg),
    }),
    qualificationTypeId: yup.number().nullable().required(requiredMsg).min(1, requiredMsg),
    educationSectorId: yup.number().nullable().when("programWithMinisterResolution", {
      is: true,
      then: yup.number().nullable(),
      otherwise: yup.number().nullable().required(requiredMsg).min(1, requiredMsg),
    }),
    educationSubSectorId: yup.number().when(["programWithMinisterResolution", "qualificationTypeId"], {
      is: (programWithMinisterResolution: boolean, qualificationTypeId: number) =>
        !programWithMinisterResolution && qualificationTypeId !== QualificationType.MicroQualification,
      then: yup.number().nullable().required(requiredMsg).min(1, requiredMsg),
      otherwise: yup.number().nullable(),
    }),
    ministerDetail: yup.object().nullable().when("programWithMinisterResolution", {
      is: true,
      then: ministerDetailSchema,
      otherwise: yup.object().nullable(),
    }),
    programWithMinisterResolution: yup.boolean(),
  }).test("durationChosen", requiredAtLeastOne, (value) => {
    if (!value?.programWithMinisterResolution) {
      return true;
    }
    const detail = value.ministerDetail;
    return [detail?.guidedLearningDuration, detail?.independentLearningDuration, detail?.workLearningDuration].some(
      (duration) => (Number(duration) || 0) > 0,
    );
  });
};